import { useEffect, useRef } from "preact/hooks";
import { cn } from "@/lib/utils";
import type { ReviewFilter } from "@/hooks/useReviews";

interface FilterChipsProps {
  filters: ReviewFilter[];
  active: string | null;
  onSelect: (key: string | null) => void;
  /** Prepend an "All" pill (sheet); the carousel row omits it. */
  showAll?: boolean;
  /** Names the row for screen readers; topic filtering unless told otherwise. */
  ariaLabel?: string;
  /** Centre a short row (product tabs) instead of starting it at the edge. */
  centered?: boolean;
}

export function FilterChips({
  filters,
  active,
  onSelect,
  showAll = false,
  ariaLabel = "Filter reviews by topic",
  centered = false,
}: FilterChipsProps) {
  const rowRef = useRef<HTMLDivElement>(null);

  // Keep the active pill visible: a sheet opened pre-filtered from a chip far
  // down the carousel row would otherwise show the selection off-screen. A
  // no-op when the pill is already fully visible. Runs once on the next frame
  // (covers in-sheet selection changes) and once after the sheet's ~300ms
  // open transition — during the animation the row hasn't reached its final
  // layout, so the mount-time attempt measures the pill as visible and does
  // nothing.
  //
  // Scrolls the row sideways only. scrollIntoView({ block: "nearest" }) also
  // scrolls the page: on a bundle PDP the product tabs start with one already
  // pressed, so every page load jumped down to the reviews (Pure Rose Trial
  // Pack and rituals, Oct 2026).
  useEffect(() => {
    if (!active) return;
    const scrollActiveIntoView = () => {
      const row = rowRef.current;
      const pill = row?.querySelector<HTMLElement>('[aria-pressed="true"]');
      if (!row || !pill) return;
      const rowBox = row.getBoundingClientRect();
      const pillBox = pill.getBoundingClientRect();
      let delta = 0;
      if (pillBox.left < rowBox.left) delta = pillBox.left - rowBox.left;
      else if (pillBox.right > rowBox.right) delta = pillBox.right - rowBox.right;
      if (delta === 0) return;
      const left = row.scrollLeft + delta;
      if (typeof row.scrollTo === "function") row.scrollTo({ left, behavior: "smooth" });
      else row.scrollLeft = left;
    };
    const frame = requestAnimationFrame(scrollActiveIntoView);
    const afterOpenTransition = setTimeout(scrollActiveIntoView, 350);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(afterOpenTransition);
    };
  }, [active]);

  if (!filters.length) return null;

  const chipClass = (isActive: boolean) =>
    cn(
      // outline-none suppresses the browser's default focus ring (Safari/iOS
      // paints a blue one) so only the custom focus-visible ring shows — the
      // keyboard-only indicator for when a user Tabs to a chip. The sheet keeps
      // its initial focus on the heading, so expanding it never rings a chip.
      "tw:shrink-0 tw:rounded-full tw:border tw:px-3 tw:py-1.5 tw:text-sm tw:font-medium tw:cursor-pointer tw:whitespace-nowrap tw:transition-colors tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-[#523c33]/40",
      isActive
        ? "tw:bg-[#fce3e4] tw:text-black tw:border-[#fce3e4]"
        : "tw:bg-white tw:text-black tw:border-[#e3dcd4] tw:hover:bg-[#f3f1f0]",
    );

  return (
    // w-full/min-w-0 matter inside centered flex columns (the sheet header):
    // without them the row sizes to its content, both edges clip, and
    // overflow-x-auto never activates — the chips look unscrollable.
    <div
      ref={rowRef}
      role="group"
      aria-label={ariaLabel}
      className={cn(
        "tw:flex tw:w-full tw:max-w-full tw:min-w-0 tw:gap-2 tw:overflow-x-auto tw:py-2 tw:[scrollbar-width:none] tw:[&::-webkit-scrollbar]:hidden",
        // safe: a row wider than the screen falls back to start-aligned, so
        // its first chip is never pushed off the scrollable left edge.
        centered ? "tw:justify-[safe_center]" : "tw:justify-start",
      )}
    >
      {showAll ? (
        <button
          type="button"
          aria-pressed={active === null}
          className={chipClass(active === null)}
          onClick={() => onSelect(null)}
        >
          All
        </button>
      ) : null}
      {filters.map((f) => (
        <button
          key={f.key}
          type="button"
          aria-pressed={active === f.key}
          className={chipClass(active === f.key)}
          onClick={() => onSelect(active === f.key ? null : f.key)}
        >
          {f.label}
        </button>
      ))}
    </div>
  );
}
